// Adquiere un Access Token de Auth0 scoped a la organizacion de la Empresa a la que se
// esta entrando, y lo deja guardado en States (via States.setCompanyToken) para que
// buildAuthorizationHeader (AuthHeaders.js) lo use en TODOS los llamados posteriores de la
// app -- no solo los de la pantalla que llamo a esta funcion.
//
// Por que hace falta: el claim "permissions" del Access Token de Auth0 solo viene poblado
// cuando el token se pide scoped a una organizacion (organization: org_id); un token
// generico (solo audience, el que se pide en withValidations.jsx antes de elegir Empresa)
// no trae permissions ni org_id. El API exige el permiso correspondiente en bloqueo
// estricto para practicamente todos los endpoints (ver los distintos *api.cs) -- la unica
// excepcion es Companies (list:Company / read:Company, ver Companiesapi.cs), que se deja
// pasar sin permiso cuando no hay org_id porque esa es justamente la pantalla que se usa
// ANTES de entrar a una Empresa. Para cualquier otra pantalla (todo lo que cuelga de
// CompaniesShow.jsx en sus 3 variantes: Operations, Adminstration, Reports) hace falta
// tener este token puesto ANTES de que se hagan llamados al API, o esos llamados van a
// fallar con 403.
//
// getAccessTokenWithPopup (no getAccessTokenSilently): es la forma que encontramos que
// funciona de manera confiable para pedir un token scoped a organization --
// getAccessTokenSilently devuelve consent_required incluso con todo lo demas bien
// configurado.
//
// El org_id real de cada Empresa ya se resuelve desde la base de datos (empresas.
// ID_Organizacion_AUTH0, agregado a pedido -- ver sp_devuelve_empresasxusuario/
// Companiesapi.cs): antes, mientras ese campo no existia, se usaba un valor quemado
// (una sola organizacion de Auth0, "Vecame", para todas las Empresas) solo para tener el
// mecanismo funcionando de punta a punta. Ver resolveCompanyOrgId mas abajo, que es lo que
// cada CompaniesShow.jsx llama ahora para obtener el org_id de la Empresa puntual que se
// esta por mostrar, antes de pedir el token scoped (fetchCompanyOrgToken).
import States from './States';

// Resuelve empresas.ID_Organizacion_AUTH0 de UNA Empresa puntual. No existe un endpoint de
// "una sola Empresa" que traiga este campo (Get_Company usa sp_devuelve_empresas, un SP
// distinto que no lo trae) -- se busca dentro de la MISMA lista que ya devuelve
// sp_devuelve_empresasxusuario (Get_Companies, Companiesapi.cs), la misma que ya consumen
// las pantallas de Listado (CompaniesList.jsx en sus 3 variantes). Ese endpoint se puede
// llamar sin ningun token scoped a organizacion (ver el comentario de permisos en
// Get_Companies): es justamente el que se usa ANTES de elegir una Empresa, por eso es
// seguro llamarlo aca, antes de fetchCompanyOrgToken.
//
// "resource" es el mismo recurso de react-admin bajo el que vive el CompaniesShow.jsx que
// llama a esto (useResourceContext(), ej. "company/administration", "company/operations",
// "company/reports") -- las 3 variantes devuelven exactamente la misma lista (misma SP), asi
// que alcanza con reusar el resource de quien llama en vez de tener que elegir uno fijo aca.
export async function resolveCompanyOrgId(dataProvider, resource, companyId) {
    try {
        const { data } = await dataProvider.getList(resource, { filter: {}, pagination: {}, sort: {} });
        const match = (data || []).find((item) => String(item.id) === String(companyId));
        return match?.idOrganizacionAuth0 || null;
    } catch (e) {
        console.warn('[CompanyAuth] No se pudo resolver el org_id de Auth0 de la Empresa:', e);
        return null;
    }
}

// Pedido de token en curso, compartido entre TODAS las llamadas concurrentes a
// fetchCompanyOrgToken (modulo-level a proposito, no por-pantalla). Hace falta porque las 3
// CompaniesShow.jsx llaman a esta funcion apenas montan, y en desarrollo React.StrictMode
// monta el efecto dos veces seguidas (ver el comentario en cada CompaniesShow.jsx) -- sin
// esta guarda, el SEGUNDO llamado disparaba un SEGUNDO getAccessTokenWithPopup mientras el
// primero seguia en curso, el navegador lo bloqueaba ("Unable to open a popup ... window.open
// returned null" -- abrir un popup sin una interaccion nueva del usuario cuenta como spam),
// y ese segundo intento FALLIDO pisaba con States.setPermissions([]) los permissions que el
// primer intento (exitoso) ya habia guardado -- por eso el sintoma era "el permiso aparece
// en el primer log pero hasPermissions igual dice que no lo tengo". Con esta guarda, el
// segundo (y cualquier otro) llamado mientras el primero sigue en curso espera el MISMO
// resultado en vez de abrir un popup nuevo.
//
// inFlightTokenOrgId (nuevo, junto con CompanyTokenOrgId en States.js): ahora que el org_id
// varia por Empresa (antes era siempre el mismo, Vecame, hardcoded), esta guarda se
// etiqueta con el org_id que esta pidiendo -- si mientras un pedido esta en curso llega
// otro para OTRO org_id (ej. el usuario entra a una Empresa distinta muy rapido), no
// corresponde esperar ese mismo resultado (seria el token de la Empresa equivocada), asi
// que se deja que arranque su propio pedido en vez de reusar la promesa en curso.
let inFlightTokenPromise = null;
let inFlightTokenOrgId = null;

// Decodifica un JWT y dice si todavia le queda vida (con 30s de margen, para no arrancar a
// usarlo justo cuando esta por vencer). No verifica la firma -- eso ya no hace falta aca,
// solo se usa para decidir si hace falta pedir uno nuevo.
function isTokenStillValid(token) {
    if (!token) {
        return false;
    }
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() + 30000;
    } catch (e) {
        return false;
    }
}

// "orgId": empresas.ID_Organizacion_AUTH0 de la Empresa a la que se esta entrando (ver
// resolveCompanyOrgId arriba) -- ya no es un valor quemado. Si viene vacio/null (la Empresa
// todavia no tiene este campo cargado en la base de datos, o resolveCompanyOrgId no la pudo
// encontrar), no tiene sentido pedir un token "scoped a nada": se avisa por consola y se
// sigue sin permissions (mismo resultado que un pedido fallido, ver el catch de abajo) en
// vez de, por ejemplo, quedar pegado al ultimo org_id usado o volver a caer en un valor por
// default -- cualquiera de esas dos alternativas terminaria mostrando los permisos de OTRA
// Empresa.
export async function fetchCompanyOrgToken(getAccessTokenWithPopup, orgId) {
    if (!orgId) {
        console.warn('[CompanyAuth] Esta Empresa no tiene org_id de Auth0 (empresas.ID_Organizacion_AUTH0) -- no se puede pedir un token scoped a organizacion.');
        States.setPermissions([]);
        return null;
    }

    // Si ya tenemos un token scoped a ESTE MISMO org_id en memoria y todavia no vencio, lo
    // reusamos directo -- SIN abrir un popup nuevo. Esto es lo que de verdad evita el
    // problema (se vio que la guarda de "pedido en curso" de abajo no alcanzaba: React
    // StrictMode en desarrollo -- o cualquier otro remontaje cercano en el tiempo de las 3
    // CompaniesShow.jsx -- puede terminar llamando a esta funcion una segunda vez cuando la
    // primera YA TERMINO, no mientras seguia en curso; ese segundo popup, sin una
    // interaccion nueva del usuario, el navegador lo bloquea ("Unable to open a popup ...
    // window.open returned null"), y el intento fallido pisaba con [] los permissions
    // buenos que la primera llamada ya habia guardado). Con este chequeo, mientras el token
    // siga vivo no se vuelve a pedir nada, sin importar cuantas veces se remonte la pantalla.
    // El chequeo de orgId (nuevo) es lo que evita reusar por error el token de OTRA Empresa:
    // antes de que el org_id variara por Empresa esto no hacia falta (un token en cache
    // siempre era el de la unica organizacion que existia, Vecame).
    const cachedToken = States.getCompanyToken();
    if (States.getCompanyTokenOrgId() === orgId && isTokenStillValid(cachedToken)) {
        return cachedToken;
    }

    if (inFlightTokenPromise && inFlightTokenOrgId === orgId) {
        return inFlightTokenPromise;
    }

    inFlightTokenOrgId = orgId;
    inFlightTokenPromise = (async () => {
        try {
            const token = await getAccessTokenWithPopup({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE ?? "",
                    organization: orgId,
                },
            });
            States.setCompanyToken(token);
            States.setCompanyTokenOrgId(orgId);

            // Decodifica el claim "permissions" del token y lo deja en States (ver
            // hasPermissions mas abajo) -- esto es lo que reemplaza el chequeo que antes
            // hacia cada pantalla contra el API (dataProvider.auth('Auth', {...})). No hace
            // falta verificar la firma aca: esto es solo para decidir que mostrar en la UI,
            // nunca la autorizacion real -- esa la sigue haciendo el backend con el token
            // completo (ver los *api.cs), que si lo valida.
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                States.setPermissions(payload.permissions || []);
            } catch (e) {
                console.warn('[CompanyAuth] No se pudo decodificar el token para leer sus permissions:', e);
                States.setPermissions([]);
            }

            return token;
        } catch (e) {
            console.warn('[CompanyAuth] No se pudo obtener el token scoped a la organizacion:', e);
            States.setPermissions([]);
            return null;
        }
    })();

    try {
        return await inFlightTokenPromise;
    } finally {
        // Libera la guarda cuando este pedido termina (haya salido bien o mal), para que la
        // PROXIMA vez que se entre a una Empresa (navegacion real, no el doble-montaje de
        // StrictMode) si pida un token nuevo.
        inFlightTokenPromise = null;
        inFlightTokenOrgId = null;
    }
}

// Reemplaza el viejo dataProvider.auth('Auth', { id: table, data: { action: actions } }):
// ese llamado iba al API, que a su vez llamaba a la Management API de Auth0 buscando
// permisos del ESQUEMA VIEJO por NIT ("una API de Auth0 por Empresa") -- completamente
// desconectado de InternalJWT, la API unica que ahora exige el backend en cada endpoint
// (ver los *api.cs). Ahora que el token scoped a la organizacion ya trae el claim
// "permissions" real (ver fetchCompanyOrgToken arriba), alcanza con mirarlo en memoria: sin
// llamados de red, y consistente con lo que el backend exige de verdad.
//
// "actions" es una lista separada por comas, igual que antes (ej: "list,create"). Devuelve
// un array de booleans en el mismo orden -- la misma forma que devolvia el endpoint viejo
// (authdata.data[0], [1], etc.) -- para no tener que tocar el codigo que ya consume ese
// resultado en cada pantalla.
export function hasPermissions(table, actions) {
    const permissions = States.getPermissions();
    return actions.split(',').map((action) => permissions.includes(`${action}:${table}`));
}
